import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal/Reveal';
import heroImage from '../assets/images/biogas-plant.png';
import storyImage from '../assets/images/hydro-power.png';
import {
  IconShieldCheck,
  IconLayers,
  IconGlobeLeaf,
  IconCompass,
  IconLeaf,
  IconRefresh,
  IconBolt,
  IconWindTurbine,
  IconSun,
  IconTransfer,
  IconCheckCircle,
  IconArrowRight,
  IconPhone,
  IconMail,
  IconLocation,
} from '../components/Icons/Icons';
import '../assets/css/about-page.css';

const STATS = [
  { value: 'UK & EU', label: 'Cross-border compliance coverage' },
  { value: 'ISCC EU & PLUS', label: 'Certification specialisms' },
  { value: 'End-to-end', label: 'From accreditation to reporting' },
  { value: 'Independent', label: 'Impartial expert assurance' },
];

const VALUES = [
  {
    icon: IconShieldCheck,
    title: 'Practical & audit-ready',
    text: 'Everything we produce is built to stand up to scrutiny, from certification bodies to regulators and auditors.',
  },
  {
    icon: IconLayers,
    title: 'End-to-end support',
    text: 'We stay with you from first accreditation through to ongoing quarterly submissions and reporting.',
  },
  {
    icon: IconGlobeLeaf,
    title: 'UK & EU expertise',
    text: 'Cross-border knowledge across Ofgem, EU RED, DENA, Nabisy and the schemes that shape your markets.',
  },
  {
    icon: IconCompass,
    title: 'Independent & impartial',
    text: 'Advice and assurance free from conflicts of interest, with guidance always in your best interest.',
  },
];

const SECTORS = [
  { icon: IconRefresh, title: 'Biomethane & Biogas', text: 'Anaerobic digestion and green gas producers navigating GGSS, GGCS and RHI.' },
  { icon: IconLeaf, title: 'Biofuels', text: 'Producers, traders and suppliers meeting RTFO and EU RED obligations.' },
  { icon: IconBolt, title: 'Sustainable Aviation Fuel', text: 'SAF producers preparing for the SAF Mandate and emerging aviation schemes.' },
  { icon: IconTransfer, title: 'Waste-derived Fuels', text: 'Waste-to-energy and waste-derived feedstocks requiring robust traceability.' },
  { icon: IconWindTurbine, title: 'Low-Carbon Energy', text: 'Operators managing UK and EU sustainability and reporting requirements.' },
  { icon: IconSun, title: 'Renewable Energy Products', text: 'Cross-sector support wherever certification and carbon reporting apply.' },
];

const SCOPE = [
  'ISCC EU & ISCC PLUS certification',
  'GAP analyses',
  'Internal audits',
  'Mass balance systems',
  'GHG calculations',
  'Supply-chain traceability',
  'Audit preparation',
  'GGSS & RTFO reporting',
  'Proof of Sustainability (PoS)',
  'NABISY & DENA compliance',
  'Supplier due diligence',
  'Independent assurance',
];

function AboutUs() {
  return (
    <main className="about-page">
      {/* ---------- Hero ---------- */}
      <section className="ap-hero">
        <div className="ap-hero-backdrop" aria-hidden="true">
          <span className="ap-glow ap-glow-teal" />
          <span className="ap-glow ap-glow-green" />
        </div>

        <div className="container ap-hero-inner">
          <div className="ap-hero-content">
            <Reveal direction="up">
              <span className="section-label">About Us</span>
            </Reveal>
            <Reveal direction="up" delay={90}>
              <h1 className="ap-hero-title">
                Turning complex regulation into <span className="ap-accent">confident market access</span>
              </h1>
            </Reveal>
            <Reveal direction="up" delay={170}>
              <p className="ap-hero-lead">
                Sustainability Advisory Ltd is a specialist consultancy for the renewable fuels,
                bioenergy and low-carbon energy sectors, guiding producers, traders and users
                through certification, compliance and reporting so their products trade with
                confidence in the UK and internationally.
              </p>
            </Reveal>
            <Reveal direction="up" delay={250}>
              <div className="ap-hero-actions">
                <Link to="/contact" className="btn btn-primary">
                  Talk to our team <IconArrowRight width={17} height={17} />
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  Our Services
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal direction="right" delay={140}>
            <figure className="ap-hero-visual">
              <div className="ap-hero-frame">
                <img src={heroImage} alt="Biogas plant with green anaerobic digester domes and processing towers" />
                <div className="ap-hero-overlay" />
                <div className="ap-hero-badge">
                  <span className="ap-hero-badge-icon">
                    <IconShieldCheck width={20} height={20} />
                  </span>
                  <span className="ap-hero-badge-text">
                    <strong>Independent &amp; Impartial</strong>
                    Renewable energy compliance
                  </span>
                </div>
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------- Stats band ---------- */}
      <section className="ap-stats">
        <div className="container ap-stats-grid">
          {STATS.map((stat, index) => (
            <Reveal direction="up" delay={index * 80} key={stat.value}>
              <div className="ap-stat">
                <span className="ap-stat-value">{stat.value}</span>
                <span className="ap-stat-label">{stat.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Story ---------- */}
      <section className="section section-white">
        <div className="container split-layout">
          <Reveal direction="left">
            <div className="ap-story-image">
              <img src={storyImage} alt="Hydroelectric weir with water flowing at sunrise" />
              <div className="ap-story-accent" />
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="ap-story-content">
              <span className="section-label">Who We Are</span>
              <h2 className="section-title">A consultancy built around renewable energy compliance</h2>
              <p className="ap-para">
                We help businesses understand and meet the sustainability requirements,
                certification schemes and regulatory obligations that govern their markets. From
                biomethane and biofuels to Sustainable Aviation Fuel and waste-derived fuels, we
                turn complex, fast-moving regulation into a clear, manageable path to compliance.
              </p>
              <p className="ap-para">
                Working alongside producers, traders and users of renewable energy, we support the
                full compliance lifecycle, getting certified, staying compliant and reporting with
                confidence. Our work spans certification readiness, GHG calculations, mass balance
                systems, traceability, audit preparation and independent assurance.
              </p>
              <div className="ap-story-tags">
                <span className="ap-tag"><IconCheckCircle width={15} height={15} /> Renewable Fuels</span>
                <span className="ap-tag"><IconCheckCircle width={15} height={15} /> Bioenergy</span>
                <span className="ap-tag"><IconCheckCircle width={15} height={15} /> Low-Carbon Energy</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Approach / values ---------- */}
      <section className="section section-tint">
        <div className="container">
          <div className="section-header">
            <Reveal direction="up">
              <span className="section-label">Our Approach</span>
              <h2 className="section-title">How we work</h2>
              <p className="section-subtitle">
                Four principles shape every engagement and every deliverable we hand over.
              </p>
            </Reveal>
          </div>

          <div className="ap-values-grid">
            {VALUES.map((value, index) => {
              const Icon = value.icon;
              return (
                <Reveal direction="up" delay={index * 80} key={value.title}>
                  <div className="ap-value-card card">
                    <span className="ap-value-icon">
                      <Icon width={24} height={24} />
                    </span>
                    <h3 className="ap-value-title">{value.title}</h3>
                    <p className="ap-value-text">{value.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Sectors ---------- */}
      <section className="section section-white">
        <div className="container">
          <div className="section-header">
            <Reveal direction="up">
              <span className="section-label">Sectors</span>
              <h2 className="section-title">Who we support</h2>
              <p className="section-subtitle">
                Specialist knowledge across the fuels and energy products where sustainability
                certification and carbon reporting matter most.
              </p>
            </Reveal>
          </div>

          <div className="ap-sectors-grid">
            {SECTORS.map((sector, index) => {
              const Icon = sector.icon;
              return (
                <Reveal direction="up" delay={(index % 3) * 80} key={sector.title}>
                  <div className="ap-sector-card card">
                    <span className="ap-sector-icon">
                      <Icon width={26} height={26} />
                    </span>
                    <div>
                      <h3 className="ap-sector-title">{sector.title}</h3>
                      <p className="ap-sector-text">{sector.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Full scope ---------- */}
      <section className="section section-tint">
        <div className="container split-layout">
          <Reveal direction="left">
            <div className="ap-scope-content">
              <span className="section-label">What We Do</span>
              <h2 className="section-title">The full scope of our support</h2>
              <p className="ap-para">
                Underpinning our services is a complete toolkit of certification, compliance and
                reporting capabilities, so whatever stage you are at, there is a clear next step.
              </p>
              <div className="ap-scope-actions">
                <Link to="/services" className="btn btn-primary">
                  Explore our services <IconArrowRight width={17} height={17} />
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal direction="right">
            <ul className="ap-scope-list">
              {SCOPE.map((item) => (
                <li key={item} className="ap-scope-item">
                  <span className="ap-scope-check">
                    <IconCheckCircle width={16} height={16} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ---------- Leadership ---------- */}
      <section className="section section-tint">
        <div className="container">
          <div className="section-header">
            <Reveal direction="up">
              <span className="section-label">Leadership</span>
              <h2 className="section-title">Led by expertise</h2>
            </Reveal>
          </div>

          <Reveal direction="up">
            <div className="ap-leader card">
              <div className="ap-leader-avatar" aria-hidden="true">EG</div>
              <div className="ap-leader-body">
                <h3 className="ap-leader-name">Evie Garner <span>MSc, AISEP</span></h3>
                <p className="ap-leader-role">Managing Director</p>
                <p className="ap-leader-text">
                  Evie leads Sustainability Advisory Ltd with deep, hands-on experience across
                  renewable fuels certification and regulatory compliance, from Ofgem schemes and
                  ISCC certification to GHG calculations and sustainability reporting. Her focus is
                  practical, audit-ready advice that helps clients stay compliant and protect their
                  market access.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="ap-cta">
        <div className="container">
          <div className="ap-cta-card">
            <span className="ap-cta-dots" aria-hidden="true" />
            <span className="ap-cta-blob" aria-hidden="true" />

            <div className="ap-cta-inner">
              <Reveal direction="up">
                <span className="section-label ap-cta-label">Get in Touch</span>
                <h2 className="ap-cta-title">Future-proof your business</h2>
                <p className="ap-cta-text">
                  Whether you need certification support, regulatory reporting or carbon reporting
                  guidance, our team is ready to help you maintain compliance and market access.
                </p>
                <div className="ap-cta-actions">
                  <a href="tel:01984624989" className="btn btn-primary">
                    <IconPhone width={17} height={17} /> Call 01984 624989
                  </a>
                  <a href="mailto:eviegarner@sustainabilityadvisors.co.uk" className="btn btn-outline-light">
                    <IconMail width={17} height={17} /> Email Us
                  </a>
                </div>
                <p className="cta-address">
                  <IconLocation width={16} height={16} /> Higher Ford, Wiveliscombe, Taunton, Somerset, TA4 2RL
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AboutUs;
